import { useEffect, useRef, useState } from 'react';

const SecondaryDropdownContainer = ({ children, isOpen, toggleDropdown, additionalclasses, isNested }) => {
    const dropdownRef = useRef(null);
    const parentScrollRef = useRef(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const handleClickOutside = (event) => {
        if (!dropdownRef.current) return;
        
        // Check if the click is inside the dropdown
        if (dropdownRef.current.contains(event.target)) {
            // Click is inside dropdown, do nothing
            return;
        }
        
        // If we reach here, click is outside of dropdown
        setTimeout(() => {
            toggleDropdown();
        }, 0);
    };

    const findScrollableParent = (element) => {
        while (element) {
            const { overflowX, overflowY } = window.getComputedStyle(element);
            if (['scroll', 'auto'].includes(overflowX) || ['scroll', 'auto'].includes(overflowY)) {
                return element;
            }
            element = element.parentElement;
        }
        return window;
    };

    const handleOverflow = () => {
        if (!dropdownRef.current) return;

        const scrollableParent = findScrollableParent(dropdownRef.current);
        parentScrollRef.current = scrollableParent;

        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const parentRect = scrollableParent === window
            ? { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth }
            : scrollableParent.getBoundingClientRect();

        const overflowTop = parentRect.top - dropdownRect.top;
        const overflowBottom = dropdownRect.bottom - parentRect.bottom;
        const overflowLeft = parentRect.left - dropdownRect.left;
        const overflowRight = dropdownRect.right - parentRect.right;

        if (overflowBottom > 0) {
            scrollableParent === window ? window.scrollBy(0, overflowBottom) : scrollableParent.scrollTop += overflowBottom;
        } else if (overflowTop > 0) {
            scrollableParent === window ? window.scrollBy(0, -overflowTop) : scrollableParent.scrollTop -= overflowTop;
        }

        if (overflowRight > 0) {
            scrollableParent === window ? window.scrollBy(overflowRight, 0) : scrollableParent.scrollLeft += overflowRight;
        } else if (overflowLeft > 0) {
            scrollableParent === window ? window.scrollBy(-overflowLeft, 0) : scrollableParent.scrollLeft -= overflowLeft;
        }
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!isOpen) return;

            const options = dropdownRef.current?.querySelectorAll('.dropdown-option');
            if (!options || options.length === 0) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    event.stopPropagation();
                    setFocusedIndex((prevIndex) => (prevIndex + 1) % options.length);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    event.stopPropagation();
                    setFocusedIndex((prevIndex) => (prevIndex - 1 + options.length) % options.length);
                    break;
                case 'Enter':
                    event.preventDefault();
                    event.stopPropagation();
                    if (focusedIndex >= 0 && focusedIndex < options.length) {
                        options[focusedIndex].click();
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    toggleDropdown();
                    break;
                default:
                    break;
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isOpen, focusedIndex]);

    useEffect(() => {
        if (focusedIndex >= 0 && dropdownRef.current) {
            const options = dropdownRef.current.querySelectorAll('.dropdown-option');
            if (options[focusedIndex]) {
                options[focusedIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [focusedIndex]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mouseup', handleClickOutside);
            requestAnimationFrame(handleOverflow);
        } else {
            document.removeEventListener('mouseup', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mouseup', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (dropdownRef.current) {
            const options = dropdownRef.current.querySelectorAll('.dropdown-option');
            options.forEach((option, index) => {
                if (index === focusedIndex) {
                    option.classList.add('focused-option');
                } else {
                    option.classList.remove('focused-option');
                }
            });
        }
    }, [focusedIndex]);

    return (
        <>
            {isOpen &&
                <div ref={dropdownRef} className={`absolute top-8 left-0 z-50 min-w-[200px] lg:min-w-[242px] bg-white-50 dark:bg-grey-600 rounded-[8px] custom-shadow grid content-start p-[4px] ignore-click overflow-y-auto scrollbar-none dropdown-container gap-[4px] ${additionalclasses}`}>
                    {children}
                </div>
            }
        </>
    );
};

export default SecondaryDropdownContainer;